import DealerV2Template, {
  DealerV2TemplateProps,
} from '@/components/template/DealerV2Template/DealerV2Template';
import { parseDealer } from '@/lib/schema/dealer/parse';
import { EditableDealerInfo, RawDealerType } from '@/lib/schema/dealer/types';
import { BasePageProps } from '@/types/page';
import { GetServerSideProps } from 'next';
import { SocialSharingTags } from '@/components/molecules/SocialSharingTags';
import { getEditableDealerInfoServerSide } from '../../lib/schema/dealer/server-fetch';
import { getUserAuthHeaderServerSide } from '../../lib/schema/user/server-fetch';

interface PageProps extends BasePageProps, DealerV2TemplateProps {}

const Page: React.FC<PageProps> = ({
  editable,
  dealer,
  dealerMetadata,
  availableMakes,
}) => {
  if (!dealer) {
    return null;
  }

  return (
    <>
      <SocialSharingTags title={`${dealer.name} | EV.com`} />
      <DealerV2Template
        editable={editable}
        dealer={dealer}
        dealerMetadata={dealerMetadata}
        availableMakes={availableMakes}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<PageProps> = async (
  context
) => {
  const { params } = context;
  const dealerId = params?.id as string;

  // get base dealer info. 404 if not found.
  const dealerInfoRes = await fetch(
    `${process.env.API_ENDPOINT}/api/dealership/dealer_info/${dealerId}`
  );
  if (dealerInfoRes.status === 404) {
    return { notFound: true };
  }
  const dealerInfo = parseDealer((await dealerInfoRes.json()) as RawDealerType);

  // get secondary dealer info
  const [editableDealerInfoResp, availableMakesRes] = await Promise.all([
    getEditableDealerInfoServerSide(dealerId),
    fetch(
      `${process.env.API_ENDPOINT}/api/inventory/listings/makes?dealershipId=${dealerId}`
    ),
  ]);

  // if user is signed in, check if they are a dealer admin
  let editable = false;
  const authHeader = await getUserAuthHeaderServerSide(context);
  if (authHeader) {
    const canEditReq = await fetch(
      `${process.env.API_ENDPOINT}/api/dealership/can_edit_dealer_info/${dealerId}`,
      {
        headers: { Authorization: authHeader },
      }
    );
    if (canEditReq.ok) {
      editable = true;
    }
  }

  const dealerMetadata: EditableDealerInfo = editableDealerInfoResp ?? {
    detailedBenefits: [],
    benefitsList: [],
    hoursOfOperation: null,
    showBenefitsSection: true,
    showHours: false,
  };

  let availableMakes: string[] = [];
  if (availableMakesRes.ok) {
    availableMakes = await availableMakesRes.json();
  }

  return {
    props: {
      editable,
      dealer: {
        id: dealerId,
        name: dealerInfo.name,
        makes: dealerInfo.makes,
        location: {
          address: dealerInfo.address,
          city: dealerInfo.city,
          state: dealerInfo.province,
          postal: dealerInfo.postalCode,
        },
        phone: dealerInfo.phone,
      },
      dealerMetadata,
      availableMakes,
      noHeader: true,
    },
  };
};

export default Page;
